/**
 * =============================================================================
 * Apps Management Page
 * =============================================================================
 *
 * Interface for managing apps in the RehaPiano platform.
 *
 * Features:
 * - List apps with filters (approval status, operational state, category,
 *   environment). Nothing is ever hidden: a rejected app and a deactivated one
 *   both stay in the table with a visible badge (BE issue #52).
 * - App developers see only their own apps
 * - System admins see all apps
 * - Navigate to app wizard for creating new apps
 * - Navigate to app detail for viewing/editing
 *
 * Workflow:
 * - DEV/TEST environments: Apps are auto-approved
 * - PROD environment: Requires admin approval
 *
 * Access: system_admin, app_admin, app_dev
 */

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { appsApi, AppListItem } from '../services/api'
import DataTable, { Column } from '../components/DataTable'
import { useAuth } from '../auth/AuthProvider'
import { at, atBoth } from '../utils/appAdminI18n'

// =============================================================================
// Status Badge Component
// =============================================================================

interface StatusBadgeProps {
  status: string
}

/**
 * Status badge with appropriate colors for each app status.
 */
const StatusBadge: React.FC<StatusBadgeProps> = ({ status }) => {
  const statusConfig: Record<string, { className: string; label: string }> = {
    draft: { className: 'bg-gray-100 text-gray-800', label: at('statusDraft') },
    pending_approval: { className: 'bg-yellow-100 text-yellow-800', label: at('statusPending') },
    approved: { className: 'bg-green-100 text-green-800', label: at('statusApproved') },
    rejected: { className: 'bg-red-100 text-red-800', label: at('statusRejected') },
    deprecated: { className: 'bg-gray-100 text-gray-600', label: at('statusDeprecated') },
  }

  const config = statusConfig[status] || { className: 'bg-gray-100 text-gray-800', label: status }

  return (
    <span className={`badge ${config.className}`}>
      {config.label}
    </span>
  )
}

/**
 * Operational state badge (BE issue #52) — shown NEXT TO the approval status,
 * never instead of it: an app can be "approved" and "deactivated" at the same
 * time, and a deactivated app must stay findable in this list.
 */
const DeactivatedBadge: React.FC = () => (
  <span
    className="badge bg-orange-100 text-orange-800"
    title={atBoth('deactivatedBannerTitle')}
    data-testid="apps-deactivated-badge"
  >
    {at('deactivatedBadge')}
  </span>
)

// =============================================================================
// Category Badge Component
// =============================================================================

interface CategoryBadgeProps {
  category: string
}

/**
 * Category badge with appropriate colors.
 */
const CategoryBadge: React.FC<CategoryBadgeProps> = ({ category }) => {
  const categoryConfig: Record<string, { className: string; label: string }> = {
    diagnostics: { className: 'bg-blue-100 text-blue-800', label: 'Diagnostics' },
    rehabilitation: { className: 'bg-purple-100 text-purple-800', label: 'Rehabilitation' },
    cognitive: { className: 'bg-indigo-100 text-indigo-800', label: 'Cognitive' },
    other: { className: 'bg-gray-100 text-gray-800', label: 'Other' },
  }

  const config = categoryConfig[category] || { className: 'bg-gray-100 text-gray-800', label: category }

  return (
    <span className={`badge ${config.className}`}>
      {config.label}
    </span>
  )
}

// =============================================================================
// Environment Badge Component
// =============================================================================

interface EnvironmentBadgeProps {
  environment: string
}

/**
 * Environment badge with appropriate colors.
 * - DEV: Yellow - development only (auto-approved)
 * - TEST: Blue - testing only (auto-approved)
 * - PROD: Green - production ready (requires approval)
 */
const EnvironmentBadge: React.FC<EnvironmentBadgeProps> = ({ environment }) => {
  const envConfig: Record<string, { className: string; label: string }> = {
    dev: { className: 'bg-yellow-100 text-yellow-800', label: 'DEV' },
    test: { className: 'bg-blue-100 text-blue-800', label: 'TEST' },
    prod: { className: 'bg-green-100 text-green-800', label: 'PROD' },
  }

  const config = envConfig[environment] || { className: 'bg-gray-100 text-gray-800', label: (environment || 'N/A').toUpperCase() }

  return (
    <span className={`badge ${config.className}`}>
      {config.label}
    </span>
  )
}

// =============================================================================
// Main Page Component
// =============================================================================

const AppsPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { hasRole } = useAuth()

  // Get filter from URL params
  const statusFilter = searchParams.get('status') || ''
  const environmentFilter = searchParams.get('environment') || ''
  // Operational switch (issue #52) — '' = all, 'active', 'inactive'.
  const activeFilter = searchParams.get('active') || ''
  const [categoryFilter, setCategoryFilter] = useState('')

  // Fetch apps
  const { data, isLoading } = useQuery({
    queryKey: ['apps', statusFilter, environmentFilter, activeFilter],
    queryFn: () => appsApi.list({
      status: statusFilter || undefined,
      environment: environmentFilter || undefined,
      active: activeFilter === '' ? undefined : activeFilter === 'active',
    }),
  })

  // Filter by category client-side
  const filteredApps = data?.data?.filter(app =>
    !categoryFilter || app.category === categoryFilter
  ) || []

  /**
   * Update environment filter in URL.
   */
  const handleEnvironmentFilterChange = (env: string) => {
    setFilters({ environment: env })
  }

  /**
   * The three URL-backed filters are independent — changing one must not drop
   * the others (issue #52 added the third).
   */
  const setFilters = (patch: Partial<{ status: string; environment: string; active: string }>) => {
    const next = {
      status: statusFilter,
      environment: environmentFilter,
      active: activeFilter,
      ...patch,
    }
    const params: Record<string, string> = {}
    if (next.status) params.status = next.status
    if (next.environment) params.environment = next.environment
    if (next.active) params.active = next.active
    setSearchParams(params)
  }

  /**
   * Format date for display.
   */
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleDateString('sk-SK', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  /**
   * Update status filter in URL.
   */
  const handleStatusFilterChange = (status: string) => {
    setFilters({ status })
  }

  /** Filter by the operational switch (issue #52). */
  const handleActiveFilterChange = (active: string) => {
    setFilters({ active })
  }

  // Table columns
  const columns: Column<AppListItem>[] = [
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      sortFn: (a, b) => a.name.localeCompare(b.name),
      searchable: true,
      searchFn: (item, query) =>
        item.name.toLowerCase().includes(query) ||
        (item.app_id || '').toLowerCase().includes(query),
      render: (app) => (
        <div>
          <div className="font-medium text-gray-900">{app.name}</div>
          <div className="text-xs text-gray-500 font-mono">{app.app_id}</div>
        </div>
      ),
    },
    {
      key: 'version',
      header: 'Version',
      sortable: true,
      render: (app) => app.current_version ?? '-',
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      sortFn: (a, b) => (a.category || '').localeCompare(b.category || ''),
      render: (app) => <CategoryBadge category={app.category || 'other'} />,
    },
    {
      key: 'environment',
      header: 'Env',
      sortable: true,
      sortFn: (a, b) => (a.environment || '').localeCompare(b.environment || ''),
      render: (app) => <EnvironmentBadge environment={app.environment || ''} />,
    },
    {
      key: 'status',
      header: 'Status',
      sortable: true,
      sortFn: (a, b) => (a.version_status || a.status).localeCompare(b.version_status || b.status),
      render: (app) => (
        <div className="flex flex-wrap gap-1">
          <StatusBadge status={app.version_status || app.status} />
          {app.is_active === false && <DeactivatedBadge />}
        </div>
      ),
    },
    {
      key: 'team',
      header: 'Team',
      sortable: true,
      sortFn: (a, b) => (a.app_team_name || '').localeCompare(b.app_team_name || ''),
      searchable: true,
      searchFn: (item, query) => (item.app_team_name || '').toLowerCase().includes(query),
      render: (app) => app.app_team_name || '-',
    },
    {
      key: 'created_at',
      header: 'Created',
      sortable: true,
      sortFn: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      render: (app) => formatDate(app.created_at),
    },
    {
      key: 'actions',
      header: 'Actions',
      render: (app) => (
        <div className="flex gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              navigate(`/apps/${app.id}`)
            }}
            className="text-primary-600 hover:text-primary-800 text-sm font-medium"
          >
            View
          </button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Apps</h2>
          <p className="text-sm text-gray-600">
            {hasRole('system_admin') || hasRole('app_admin')
              ? 'Manage and approve apps for the RehaPiano platform.'
              : 'Manage your apps and track their approval status.'}
          </p>
        </div>
        {(hasRole('app_dev') || hasRole('system_admin') || hasRole('app_admin')) && (
          <button
            onClick={() => navigate('/apps/new')}
            className="btn-primary"
          >
            Add App
          </button>
        )}
      </div>

      {/* Stats Cards for Admin */}
      {(hasRole('system_admin') || hasRole('app_admin')) && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <StatsCard
            label="Total Apps"
            value={data?.data?.length || 0}
            color="bg-gray-100"
          />
          <StatsCard
            label="Pending Approval"
            value={data?.data?.filter(a => a.status === 'pending_approval').length || 0}
            color="bg-yellow-100"
            onClick={() => handleStatusFilterChange('pending_approval')}
          />
          <StatsCard
            label="Approved"
            value={data?.data?.filter(a => a.status === 'approved').length || 0}
            color="bg-green-100"
            onClick={() => handleStatusFilterChange('approved')}
          />
          <StatsCard
            label="PROD Apps"
            value={data?.data?.filter(a => a.environment === 'prod' && a.status === 'approved').length || 0}
            color="bg-purple-100"
            onClick={() => handleEnvironmentFilterChange('prod')}
          />
          {/* Operational switch (issue #52) — deactivated apps must stay
              visible and reachable, not silently drop out of the portal. */}
          <StatsCard
            label={at('statsDeactivated')}
            value={data?.data?.filter(a => a.is_active === false).length || 0}
            color="bg-orange-100"
            onClick={() => handleActiveFilterChange('inactive')}
          />
        </div>
      )}

      {/* Filters */}
      <div className="card">
        <div className="flex flex-wrap gap-4">
          {/* Status Filter */}
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              className="input"
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
            >
              <option value="">All statuses</option>
              <option value="draft">{at('statusDraft')}</option>
              <option value="pending_approval">{at('statusPending')}</option>
              <option value="approved">{at('statusApproved')}</option>
              <option value="rejected">{at('statusRejected')}</option>
              <option value="deprecated">{at('statusDeprecated')}</option>
            </select>
          </div>

          {/* Operational state filter (issue #52) — a second, independent axis:
              an app can be approved AND deactivated. */}
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {at('filterActivationLabel')}
            </label>
            <select
              className="input"
              value={activeFilter}
              onChange={(e) => handleActiveFilterChange(e.target.value)}
              title={atBoth('filterActivationLabel')}
              data-testid="apps-active-filter"
            >
              <option value="">{at('filterActivationAll')}</option>
              <option value="active">{at('filterActivationActive')}</option>
              <option value="inactive">{at('filterActivationInactive')}</option>
            </select>
          </div>

          {/* Category Filter */}
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Category
            </label>
            <select
              className="input"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All categories</option>
              <option value="diagnostics">Diagnostics</option>
              <option value="rehabilitation">Rehabilitation</option>
              <option value="cognitive">Cognitive</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Environment Filter */}
          <div className="w-48">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Environment
            </label>
            <select
              className="input"
              value={environmentFilter}
              onChange={(e) => handleEnvironmentFilterChange(e.target.value)}
            >
              <option value="">All environments</option>
              <option value="dev">DEV - Development</option>
              <option value="test">TEST - Testing</option>
              <option value="prod">PROD - Production</option>
            </select>
          </div>

          {/* Clear Filters */}
          {(statusFilter || categoryFilter || environmentFilter || activeFilter) && (
            <div className="flex items-end">
              <button
                onClick={() => {
                  setSearchParams({})
                  setCategoryFilter('')
                }}
                className="btn-secondary text-sm"
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={filteredApps}
        loading={isLoading}
        getRowKey={(app) => app.id}
        emptyMessage="No apps found"
        searchable
        searchPlaceholder="Search by name, app ID, team..."
        onRowClick={(app) => navigate(`/apps/${app.id}`)}
      />
    </div>
  )
}

// =============================================================================
// Stats Card Component
// =============================================================================

interface StatsCardProps {
  label: string
  value: number
  color: string
  onClick?: () => void
}

const StatsCard: React.FC<StatsCardProps> = ({ label, value, color, onClick }) => {
  return (
    <div
      className={`${color} rounded-lg p-4 ${onClick ? 'cursor-pointer hover:opacity-80 transition-opacity' : ''}`}
      onClick={onClick}
    >
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-sm text-gray-600">{label}</div>
    </div>
  )
}

export default AppsPage
